'use client';

import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import { expensesApi } from '@/lib/api';
import type { CreditCardCockpitResponse, CreditCockpitQuery } from '@/lib/api';
import type { PeriodSelection } from '@/lib/period-presets';
import { useAuthedFetcher } from './swrConfig';

const CREDIT_COCKPIT_SWR_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 1_000,
  keepPreviousData: true,
} as const;

export function buildCreditCockpitQuery(period: PeriodSelection): CreditCockpitQuery {
  return {
    from: format(period.from, 'yyyy-MM-dd'),
    to: format(period.to, 'yyyy-MM-dd'),
    period: period.preset,
  };
}

export function creditCockpitKey(period: PeriodSelection) {
  const query = buildCreditCockpitQuery(period);
  return ['credit-cockpit', query.period, query.from, query.to] as const;
}

export function useCreditCockpit(period: PeriodSelection) {
  const authedFetch = useAuthedFetcher();
  const { from, preset, to } = period;
  const query = useMemo(
    () => ({
      from: format(from, 'yyyy-MM-dd'),
      to: format(to, 'yyyy-MM-dd'),
      period: preset,
    }),
    [from, preset, to],
  );
  const controllerRef = useRef<AbortController | null>(null);
  const fetchCockpit = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      return await authedFetch((token) => expensesApi.getCockpit(token, query, controller.signal));
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  }, [authedFetch, query]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  return useSWR<CreditCardCockpitResponse>(
    creditCockpitKey(period),
    fetchCockpit,
    CREDIT_COCKPIT_SWR_OPTIONS,
  );
}
